import React, { useState } from 'react';
import { Box, Menu, MenuItem, Tooltip, Typography, Divider } from '@mui/material';
import { AddCircleOutline, ArrowDropDown } from '@mui/icons-material';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';

/**
 * Self-contained dropdown component for combo selection
 * Manages its own menu state - no parent involvement needed
 */
const ComboDropdown = ({ 
  comboPrices = [],          // Array of combo options
  selectedCombo,             // Currently selected combo
  onComboSelect,             // Callback when combo changes
  onSelectNewProduct,        // Callback for "Select new product" option
  productKey,                // Unique identifier
  disabled = false,          // For running/finished experiments
  t                          // Translation function
}) => {
  // Own state - completely self-contained
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleSelect = (combo) => {
    onComboSelect(productKey, combo);
    handleClose();
  };
  
  const handleSelectNewProduct = () => {
    if (onSelectNewProduct) {
      onSelectNewProduct(productKey);
    }
    handleClose();
  };

  return (
    <>
      {/* Dropdown Trigger */}
      <Tooltip title={selectedCombo?.name} arrow placement="top">
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            px: '4px',
            py: '1px',
            backgroundColor: disabled ? colors.background.disabled : colors.background.surface,
            border: `1px solid ${colors.border.medium}`,
            borderRadius: '4px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            minWidth: '100px',
            maxWidth: '130px',
            '&:hover': !disabled ? {
              backgroundColor: colors.background.subtle,
              borderColor: colors.border.medium
            } : {},
            opacity: disabled ? 0.6 : 1
          }}
        >
          <Typography sx={{
            fontSize: '10px',
            fontWeight: 700,
            lineHeight: 1.2,
            color: disabled ? colors.text.disabled : colors.text.primary,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {selectedCombo?.name || t('selectCombo') || 'Select combo'}
          </Typography>
          <ArrowDropDown sx={{ 
            fontSize: '12px', 
            color: disabled ? colors.text.disabled : colors.text.secondary,
            fontWeight: 'bold',
            flexShrink: 0
          }} />
        </Box>
      </Tooltip>
      
      {/* Self-contained Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: `1px solid ${colors.border.light}`,
            boxShadow: `0 4px 20px ${colors.shadow.soft}`,
            mt: '4px',
            minWidth: '200px'
          }
        }}
      >
        {comboPrices.map((combo, index) => {
          // Create candidate object in format expected
          const candidate = {
            name: combo.candidateName,
            price: combo.totalWithDiscount.toFixed(2)
          };
          
          return (
            <MenuItem
              key={index}
              onClick={() => handleSelect(candidate)}
              sx={{
                ...responsiveTypography.small,
                py: '6px',
                px: '12px',
                '&:hover': {
                  backgroundColor: colors.background.subtle
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Tooltip title={combo.candidateName} arrow placement="left">
                  <Typography sx={{
                    ...responsiveTypography.small,
                    fontWeight: 500,
                    color: colors.text.primary,
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {combo.candidateName}
                  </Typography>
                </Tooltip>
                <Typography sx={{
                  ...responsiveTypography.small,
                  fontWeight: 700,
                  color: colors.text.primary,
                  ml: responsiveSpacing.sm,
                  flexShrink: 0
                }}>
                  €{combo.totalWithDiscount.toFixed(2)}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
        
        {/* Add divider and 'Select a new product' option if callback provided */}
        {onSelectNewProduct && comboPrices.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={handleSelectNewProduct}
              sx={{
                ...responsiveTypography.small,
                py: '8px',
                px: '12px',
                '&:hover': {
                  backgroundColor: colors.background.subtle
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddCircleOutline sx={{ fontSize: '16px', color: colors.text.secondary }} />
                <Typography sx={{
                  ...responsiveTypography.small,
                  fontStyle: 'italic',
                  color: colors.text.secondary
                }}>
                  {t('selectNewProduct') || 'Select a new product...'}
                </Typography>
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ComboDropdown;